/**
 * WebSocket client para streaming de transcripción en tiempo real.
 *
 * Protocolo:
 * - Frontend → Backend: frames binarios [1 byte canal + PCM16 audio]
 * - Backend → Frontend: frames JSON con TranscriptionWSMessage
 *
 * El backend reenvía los chunks de audio a Deepgram Streaming API
 * y retorna los resultados de transcripción parcial/final.
 */

import { config } from '@/config/constants';
import { logger } from '@/utils/logger';
import TokenStorage from '@/utils/tokenStorage';
import type {
  TranscriptionWSMessage,
  TranscriptionWSStatus,
  TranscriptionSegment,
  TranscriptionChannel,
} from '@/types/transcription';
import { ensureAccessTokenForWebSocket } from '@/services/api';
import { packAudioChunk } from './audioCapture/AudioCaptureService';

/** Construye la URL del WS de transcripción (tests + mismo criterio que connect). */
export function buildTranscriptionWsUrl(
  apiBase: string,
  callId: string,
  token: string | null,
): string {
  const base = apiBase.replace(/\/$/, '');
  const wsBase = base
    .replace(/^https:\/\//, 'wss://')
    .replace(/^http:\/\//, 'ws://');
  const q = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${wsBase}/ws/transcription/${callId}${q}`;
}

type StatusCallback = (status: TranscriptionWSStatus) => void;
type TranscriptionCallback = (segment: TranscriptionSegment) => void;

let segmentIdCounter = 0;

export class TranscriptionWebSocket {
  private ws: WebSocket | null = null;
  private callId: string;
  private status: TranscriptionWSStatus = 'idle';
  private statusCallbacks: StatusCallback[] = [];
  private transcriptionCallbacks: TranscriptionCallback[] = [];
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private keepaliveTimer: ReturnType<typeof setInterval> | null = null;
  private pendingChunks: ArrayBuffer[] = [];
  /** Timestamp del último chunk de audio enviado (para keepalive adaptativo). */
  private _lastAudioSentAt = 0;
  /** Intentos de reconexión automática (reset a 0 en connect exitoso). */
  private _reconnectAttempts = 0;
  private static readonly MAX_RECONNECT = 3;
  private static readonly RECONNECT_DELAYS_MS = [2_000, 5_000, 15_000] as const;

  constructor(callId: string) {
    this.callId = callId;
  }

  /**
   * Conecta al WebSocket de transcripción del backend.
   * Refresca JWT si hace falta (misma lógica que Axios); no usa API key en query.
   * @returns false si no hay sesión JWT válida (p. ej. sólo VITE_API_BEARER_KEY).
   */
  async connect(): Promise<boolean> {
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) {
      console.warn('[TranscriptionWS] Ya conectado o conectando');
      return true;
    }

    this.setStatus('connecting');

    let token: string | null;
    try {
      token = await ensureAccessTokenForWebSocket();
    } catch (e) {
      console.warn('[TranscriptionWS] Error obteniendo token:', e);
      this.setStatus('error');
      return false;
    }

    if (!token) {
      const apiKeyOnly = !TokenStorage.getAccessToken() && !!config.API_BEARER_KEY;
      console.warn(
        '[TranscriptionWS]',
        apiKeyOnly
          ? 'Sin JWT: la transcripción en vivo requiere sesión de usuario (la API key del CRM no aplica al WebSocket).'
          : 'Sin sesión válida para transcripción en vivo.',
      );
      this.setStatus('error');
      return false;
    }

    const url = buildTranscriptionWsUrl(config.API_BASE_URL, this.callId, token);

    console.warn('[TranscriptionWS] 🔌 Conectando a:', url.replace(/token=[^&]+/, 'token=***'));

    this.attachWebSocketHandlers(url);
    return true;
  }

  /** Abre el WebSocket y registra handlers (reutilizado tras reconexión). */
  private attachWebSocketHandlers(url: string): void {
    this.ws = new WebSocket(url);
    this.ws.binaryType = 'arraybuffer';

    this.ws.onopen = () => {
      this.setStatus('connected');
      console.warn('[TranscriptionWS] ✅ WebSocket conectado');

      // Enviar chunks pendientes
      for (const chunk of this.pendingChunks) {
        this.ws?.send(chunk);
      }
      this.pendingChunks = [];

      this._reconnectAttempts = 0; // conexión exitosa — resetear contador
      this._lastAudioSentAt = 0;

      // Keepalive adaptativo cada 15s:
      // – Si no hubo audio en los últimos 15s → enviar frame de silencio para
      //   mantener activa la sesión Deepgram (idle-timeout ~60s sin audio).
      // – Si hubo audio reciente → ping JSON normal.
      this.keepaliveTimer = setInterval(() => {
        if (this.ws?.readyState !== WebSocket.OPEN) return;
        const msSinceAudio = this._lastAudioSentAt > 0
          ? Date.now() - this._lastAudioSentAt
          : Infinity;
        if (msSinceAudio > 15_000) {
          // 160 muestras × 2 bytes = 320 bytes = 10ms de silencio a 16kHz PCM16
          const silence = new ArrayBuffer(320);
          const packed = packAudioChunk(silence, 'agent');
          this.ws.send(packed);
          if (import.meta.env.DEV) {
            console.warn('[TranscriptionWS] 🔇 Silencio enviado (keepalive Deepgram)');
          }
        } else {
          this.ws.send(JSON.stringify({ type: 'keepalive' }));
        }
      }, 15_000);
    };

    this.ws.onmessage = (event) => {
      try {
        const msg: TranscriptionWSMessage = JSON.parse(event.data as string);
        if (msg.type === 'transcription') {
          console.warn('[TranscriptionWS] 📝 Transcripción recibida:', msg.channel, msg.is_final ? '(final)' : '(parcial)', msg.transcript?.slice(0, 60));
        } else {
          console.warn('[TranscriptionWS] 📨 Mensaje:', msg.type, msg);
        }
        this.handleMessage(msg);
      } catch {
        console.warn('[TranscriptionWS] ⚠️ Mensaje no parseable:', event.data);
      }
    };

    this.ws.onerror = (event) => {
      console.error('[TranscriptionWS] ❌ Error WebSocket:', event);
      console.warn('[TranscriptionWS] 💡 Si el WS no conecta, verificar: 1) Backend corriendo 2) DEEPGRAM_API_KEY en env del backend 3) URL correcta');
      this.setStatus('error');
    };

    this.ws.onclose = (event) => {
      // Códigos: 1000=normal, 1006=abnormal(sin close frame), 1008=policy, 1011=server error
      const codeInfo = event.code === 1006 ? '(conexión cortada/servidor no alcanzable)'
        : event.code === 1008 ? '(policy violation — ¿token inválido?)'
        : event.code === 1011 ? '(error interno del servidor — ¿DEEPGRAM_API_KEY configurada?)'
        : '';
      console.warn(`[TranscriptionWS] 🔴 Cerrado: code=${event.code} ${codeInfo} reason=${event.reason}`);
      this.cleanupTimers();

      if (this.status === 'idle') return;
      this.setStatus('disconnected');

      // Auto-reconectar en cierres anormales (no 1000=normal ni 1008=token inválido)
      const isAbnormal = event.code !== 1000 && event.code !== 1008;
      if (isAbnormal && this._reconnectAttempts < TranscriptionWebSocket.MAX_RECONNECT) {
        this._scheduleReconnect();
      }
    };
  }

  /**
   * Envía un chunk de audio binario al backend.
   * Si el WebSocket aún no está conectado, encola el chunk.
   */
  private _chunksSent = 0;

  sendAudioChunk(audioData: ArrayBuffer, channel: TranscriptionChannel): void {
    const packed = packAudioChunk(audioData, channel);

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(packed);
      this._chunksSent++;
      this._lastAudioSentAt = Date.now();
      // Log cada 50 chunks (~12.5s) para no saturar consola
      if (this._chunksSent % 50 === 1) {
        console.warn(`[TranscriptionWS] 🎤 Audio chunk #${this._chunksSent} enviado (canal: ${channel}, ${audioData.byteLength} bytes)`);
      }
    } else if (this.ws?.readyState === WebSocket.CONNECTING) {
      // Encolar mientras se conecta (limitar a 50 chunks ~12.5s de audio)
      if (this.pendingChunks.length < 50) {
        this.pendingChunks.push(packed);
      }
    } else {
      if (this._chunksSent === 0) {
        console.warn('[TranscriptionWS] ⚠️ Intentando enviar audio pero WS no está abierto. readyState:', this.ws?.readyState);
      }
    }
  }

  /**
   * Registra callback para recibir segmentos de transcripción.
   */
  onTranscription(callback: TranscriptionCallback): () => void {
    this.transcriptionCallbacks.push(callback);
    return () => {
      this.transcriptionCallbacks = this.transcriptionCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  /**
   * Registra callback para cambios de estado del WebSocket.
   */
  onStatusChange(callback: StatusCallback): () => void {
    this.statusCallbacks.push(callback);
    return () => {
      this.statusCallbacks = this.statusCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  /**
   * Cierra la conexión WebSocket.
   */
  disconnect(): void {
    this.setStatus('idle');
    this.cleanupTimers();
    this.pendingChunks = [];

    if (this.ws) {
      this.ws.onclose = null; // Evitar trigger de reconnect
      if (this.ws.readyState <= WebSocket.OPEN) {
        this.ws.close(1000, 'Client disconnect');
      }
      this.ws = null;
    }
  }

  /**
   * Estado actual de la conexión.
   */
  getStatus(): TranscriptionWSStatus {
    return this.status;
  }

  // ─── Private ────────────────────────────────────────────────────────────────

  private handleMessage(msg: TranscriptionWSMessage): void {
    switch (msg.type) {
      case 'transcription': {
        const text = (msg.transcript ?? (msg as { text?: string }).text ?? '').trim();
        if (!text) return;

        const segment: TranscriptionSegment = {
          id: `seg-${++segmentIdCounter}`,
          channel: msg.channel ?? 'unknown',
          text,
          isFinal: msg.is_final ?? false,
          timestamp: Date.now(),
          confidence: msg.confidence,
          start: msg.start,
          duration: msg.duration,
        };

        for (const cb of this.transcriptionCallbacks) {
          cb(segment);
        }
        break;
      }

      case 'status':
        logger.info('[TranscriptionWS] Status:', msg.status);
        break;

      case 'error':
        logger.error('[TranscriptionWS] Error del servidor:', msg.error);
        break;

      case 'keepalive':
        // ACK del keepalive, no hacer nada
        break;

      default:
        logger.warn('[TranscriptionWS] Tipo de mensaje desconocido:', msg);
    }
  }

  private setStatus(status: TranscriptionWSStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const cb of this.statusCallbacks) {
      cb(status);
    }
  }

  private _scheduleReconnect(): void {
    const attempt = this._reconnectAttempts;
    const delay = TranscriptionWebSocket.RECONNECT_DELAYS_MS[attempt] ?? 15_000;
    this._reconnectAttempts = attempt + 1;
    console.warn(`[TranscriptionWS] 🔄 Reconectando WS transcripción (intento ${attempt + 1}/${TranscriptionWebSocket.MAX_RECONNECT}) en ${delay}ms`);

    this.reconnectTimer = setTimeout(() => {
      if (this.status === 'idle') return; // disconnect() fue llamado mientras esperábamos
      void this.connect().then((ok) => {
        if (!ok) {
          console.warn('[TranscriptionWS] Reconexión cancelada: sin token válido');
        }
      });
    }, delay);
  }

  private cleanupTimers(): void {
    if (this.keepaliveTimer) {
      clearInterval(this.keepaliveTimer);
      this.keepaliveTimer = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
